import { useState } from 'react';

import { api } from '../api/client';
import type { OrgUnitRow, Page, RoleRow, UserRow } from '../api/types';
import { Badge, Button, Card, Field, Form, Loading, Modal, Notice, Select, Table } from '../components';
import { useAction, useResource } from '../hooks';
import { useSession } from '../auth/session';
import { formatDate } from '../format';
import { TagPicker } from './Tags';

/**
 * People who use the product.
 *
 * The password rules here are the ones SMA got most wrong. A generated password is shown
 * **once**, at the moment it is created, and never again — it is stored only as a hash, so
 * there is nothing to show later. A profile edit cannot touch credentials. Resetting is a
 * separate, deliberate action that produces a new value every time; SMA reset every
 * password to the same fixed string, so knowing it once meant knowing it always (S-3).
 */
export function Users(): JSX.Element {
  const { can, config } = useSession();
  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<UserRow | null>(null);
  const [issued, setIssued] = useState<{ username: string; password: string } | null>(null);

  const query = search === '' ? '' : `?search=${encodeURIComponent(search)}`;
  const users = useResource(() => api.get<Page<UserRow>>(`/users${query}`), [search]);
  const roles = useResource(() => api.get<RoleRow[]>('/roles'), []);
  const orgUnits = useResource(() => api.get<OrgUnitRow[]>('/org-units'), []);

  const roleName = (id: string): string =>
    (roles.data ?? []).find((role) => role.id === id)?.name ?? '—';

  return (
    <>
      {issued !== null && (
        <Notice kind="success" onDismiss={() => setIssued(null)}>
          Password for <strong>{issued.username}</strong>: <code>{issued.password}</code> — copy it
          now. It is stored only as a hash and cannot be shown again.
        </Notice>
      )}

      <Card
        title={`${config.fieldUserLabel}s`}
        actions={can('user.edit') ? <Button onClick={() => setCreating(true)}>Add person</Button> : undefined}
      >
        <div className="toolbar">
          <Field label="Search" value={search} onChange={setSearch} placeholder="Name or username" />
        </div>

        {users.loading ? (
          <Loading />
        ) : users.error !== null ? (
          <Notice kind="error">{users.error}</Notice>
        ) : (
          <Table
            rows={users.data?.items ?? []}
            keyOf={(row) => row.id}
            empty="Nobody matches."
            onRowClick={can('user.edit') ? (row) => setEditing(row) : undefined}
            columns={[
              { header: 'Username', render: (row) => row.username },
              { header: 'Name', render: (row) => row.fullName },
              { header: 'Role', render: (row) => roleName(row.roleId) },
              { header: 'Email', render: (row) => row.email ?? '—' },
              {
                header: 'Last signed in',
                render: (row) =>
                  row.lastLoginAt === null ? (
                    <span className="muted">Never</span>
                  ) : (
                    formatDate(row.lastLoginAt)
                  ),
              },
              {
                header: 'Status',
                render: (row) => (
                  <div className="row">
                    <Badge tone={row.isActive ? 'accepted' : 'blocked'}>
                      {row.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                    {row.mustChangePassword && <Badge tone="flagged">Must change password</Badge>}
                  </div>
                ),
              },
            ]}
          />
        )}
      </Card>

      {creating && (
        <UserForm
          title="Add person"
          roles={roles.data ?? []}
          orgUnits={orgUnits.data ?? []}
          onClose={() => setCreating(false)}
          onCreated={(username, password) => {
            setCreating(false);
            setIssued({ username, password });
            users.reload();
          }}
        />
      )}

      {editing !== null && (
        <UserForm
          title={editing.fullName}
          user={editing}
          roles={roles.data ?? []}
          orgUnits={orgUnits.data ?? []}
          onClose={() => setEditing(null)}
          onCreated={() => {
            setEditing(null);
            users.reload();
          }}
          onReset={(password) => {
            setEditing(null);
            setIssued({ username: editing.username, password });
            users.reload();
          }}
        />
      )}
    </>
  );
}

function UserForm({
  title,
  user,
  roles,
  orgUnits,
  onClose,
  onCreated,
  onReset,
}: {
  title: string;
  user?: UserRow;
  roles: RoleRow[];
  orgUnits: OrgUnitRow[];
  onClose: () => void;
  onCreated: (username: string, password: string) => void;
  onReset?: (password: string) => void;
}): JSX.Element {
  const { run, error, busy } = useAction();
  const [username, setUsername] = useState(user?.username ?? '');
  const [fullName, setFullName] = useState(user?.fullName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [mobile, setMobile] = useState(user?.mobile ?? '');
  const [roleId, setRoleId] = useState(user?.roleId ?? '');
  const [orgUnitId, setOrgUnitId] = useState(user?.orgUnitId ?? '');

  const submit = async (): Promise<void> => {
    const body = {
      fullName,
      roleId,
      ...(email === '' ? {} : { email }),
      ...(mobile === '' ? {} : { mobile }),
      ...(orgUnitId === '' ? {} : { orgUnitId }),
    };

    const ok = await run(async () => {
      if (user === undefined) {
        const created = await api.post<{ id: string; initialPassword: string }>('/users', {
          username,
          ...body,
        });
        onCreated(username, created.initialPassword);
      } else {
        // No credential fields here at all. SMA's equivalent rewrote username and password
        // on an ordinary profile save (S-4); the way to make that impossible is for the
        // request not to carry them.
        await api.patch(`/users/${user.id}`, body);
        onCreated(username, '');
      }
    });

    if (!ok) return;
  };

  const reset = async (): Promise<void> => {
    if (user === undefined || onReset === undefined) return;

    await run(async () => {
      const result = await api.post<{ temporaryPassword: string }>(
        `/users/${user.id}/reset-password`,
      );
      onReset(result.temporaryPassword);
    });
  };

  return (
    <Modal title={title} onClose={onClose}>
      {error !== null && <Notice kind="error">{error}</Notice>}

      <Form onSubmit={() => void submit()}>
        <div className="form-row">
          <Field
            label="Username"
            value={username}
            onChange={setUsername}
            required
            hint={user === undefined ? 'Lowercased automatically' : 'Cannot be changed'}
          />
          <Field label="Full name" value={fullName} onChange={setFullName} required />
        </div>

        <div className="form-row">
          <Field label="Email" value={email ?? ''} onChange={setEmail} type="email" />
          <Field label="Mobile" value={mobile ?? ''} onChange={setMobile} />
        </div>

        <div className="form-row">
          <Select
            label="Role"
            value={roleId}
            onChange={setRoleId}
            required
            options={roles.map((role) => ({ value: role.id, label: role.name }))}
          />
          <Select
            label="Organisation unit"
            value={orgUnitId ?? ''}
            onChange={setOrgUnitId}
            placeholder="Whole organisation"
            options={orgUnits.map((unit) => ({
              value: unit.id,
              label: `${'— '.repeat(Math.max(0, unit.depth - 1))}${unit.name}`,
            }))}
          />
        </div>

        {user !== undefined && (
          <div className="field">
            <span className="field-label">Where this person can be sent</span>
            <TagPicker ownerId={user.id} kind="user" />
          </div>
        )}

        {user === undefined && (
          <Notice kind="info">
            A password will be generated and shown once. It is stored only as a hash, so it
            cannot be retrieved afterwards.
          </Notice>
        )}

        <div className="form-actions">
          <Button type="submit" disabled={busy || username === '' || fullName === '' || roleId === ''}>
            {busy ? 'Saving…' : 'Save'}
          </Button>
          {user !== undefined && onReset !== undefined && (
            <Button variant="secondary" onClick={() => void reset()} disabled={busy}>
              Reset password
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </Form>
    </Modal>
  );
}
